import { useEffect, useMemo, useState } from "react";
import { createPortal } from "react-dom";
import API_BASE from "../../config";
import { extractErrorMessage } from "../../utils/apiError";
import s from "./SubjectsByGradePanel.module.css";

function authHeaders() {
  let user = null;
  try { user = JSON.parse(sessionStorage.getItem("user") || localStorage.getItem("user") || "null"); }
  catch { /* ignore */ }
  return {
    "Content-Type": "application/json",
    "X-User-Role":  user?.role     || "",
    "X-Username":   user?.username || "",
    ...(user?.linkedEntityId != null ? { "X-Linked-Entity-Id": String(user.linkedEntityId) } : {}),
    ...(user?.linkedEntityType     ? { "X-Linked-Entity-Type": user.linkedEntityType } : {}),
  };
}

/**
 * Grade-level-grouped view of the subject catalog.
 * Replaces the flat subjects table with:
 *   - a card per grade level showing its subject count,
 *   - a modal on click listing that grade's subjects with edit/delete,
 *   - an inline quick-add form that opens inside the same modal so admins
 *     don't have to leave the grade context to add another subject.
 *
 * Props:
 *   subjects       Array<{id, code, title, units, gradeLevelId, departmentId}>
 *   gradeLevels    Array<{id, name, departmentId, ordering}>
 *   faculties      Array<{id, name}>  — TEACHING-tagged departments. The
 *                                       prop name is kept for compatibility
 *                                       (it labels the parent academic
 *                                       grouping); after the Faculty→
 *                                       Department merge these are just
 *                                       Department rows where staffType=
 *                                       TEACHING.
 *   onEdit         fn(subjectRow)             — open the edit form
 *   onDelete       fn(subjectRow)             — delete after confirm (caller owns confirm)
 *   onAfterCreate  fn()                       — parent refreshes subject list after a create
 */
export default function SubjectsByGradePanel({ subjects, gradeLevels, faculties, onEdit, onDelete, onAfterCreate }) {
  const [openGradeId, setOpenGradeId] = useState(null); // number | "ungraded" | null

  // Two views: grade-level cards (default) and a flat searchable table.
  const [viewMode, setViewMode] = useState("cards"); // "cards" | "table"
  const [search, setSearch] = useState("");
  // Sort state for the flat "All Subjects" table. Defaults to grade-then-
  // code (matching the original natural order). Click any column header
  // to toggle ascending / descending; clicking a different column resets
  // direction to the column's natural default (text → asc, number → desc).
  const [sortKey, setSortKey] = useState("grade");
  const [sortDir, setSortDir] = useState("asc");

  // Standalone "new subject" modal used only in table view (grade is picked
  // inside the form since there's no grade-card context to inherit from).
  const [newOpen, setNewOpen] = useState(false);
  const [newGradeId, setNewGradeId] = useState("");

  // Per-subject class-offering counts (used by the table view). Fetched once
  // on mount — admin-only panel, so fresh counts when they arrive is fine.
  const [offerings, setOfferings] = useState([]);
  useEffect(() => {
    let cancelled = false;
    fetch(`${API_BASE}/api/class-offerings`)
      .then(r => r.ok ? r.json() : [])
      .then(list => { if (!cancelled) setOfferings(list || []); })
      .catch(() => {});
    return () => { cancelled = true; };
  }, []);

  // Inline quick-add state (form shown inside the modal when user clicks
  // "+ Add subject to <grade>"). Free-text Code/Title/Units so admins can
  // create brand-new subjects for this grade, with an optional "Copy from
  // existing" shortcut that pre-fills the fields from another grade's subject.
  const [addOpen,  setAddOpen]  = useState(false);
  const [addCode,  setAddCode]  = useState("");
  const [addTitle, setAddTitle] = useState("");
  const [addUnits, setAddUnits] = useState("");
  const [addBusy,  setAddBusy]  = useState(false);
  const [addError, setAddError] = useState("");

  useEffect(() => {
    setAddOpen(false);
    setAddCode(""); setAddTitle(""); setAddUnits("");
    setAddError(""); setAddBusy(false);
  }, [openGradeId]);

  // Escape closes the grade's subjects modal (outside-click no longer does).
  useEffect(() => {
    if (openGradeId == null) return;
    function onKey(e) { if (e.key === "Escape") setOpenGradeId(null); }
    document.addEventListener("keydown", onKey);
    return () => document.removeEventListener("keydown", onKey);
  }, [openGradeId]);

  // Escape closes the standalone "new subject" modal (outside-click no
  // longer does).
  useEffect(() => {
    if (!newOpen) return;
    function onKey(e) { if (e.key === "Escape") setNewOpen(false); }
    document.addEventListener("keydown", onKey);
    return () => document.removeEventListener("keydown", onKey);
  }, [newOpen]);

  async function saveNewSubject(grade) {
    if (!addCode.trim() || !addTitle.trim()) {
      setAddError("Code and title are required.");
      return false;
    }
    setAddBusy(true); setAddError("");
    try {
      const payload = {
        code:  addCode.trim(),
        title: addTitle.trim(),
        units: addUnits === "" ? null : Number(addUnits),
        gradeLevelId: Number(grade.id),
        departmentId: grade.departmentId != null ? Number(grade.departmentId) : null,
      };
      const res = await fetch(`${API_BASE}/api/subjects`, {
        method: "POST",
        headers: authHeaders(),
        body: JSON.stringify(payload),
      });
      if (!res.ok) throw new Error(await extractErrorMessage(res, `Save failed (${res.status})`));
      setAddOpen(false);
      setAddCode(""); setAddTitle(""); setAddUnits("");
      onAfterCreate && onAfterCreate();
      return true;
    } catch (e) {
      setAddError(e.message);
      return false;
    } finally {
      setAddBusy(false);
    }
  }

  async function saveNewFromTable() {
    if (!newGradeId) { setAddError("Pick a grade level."); return; }
    const grade = (gradeLevels || []).find(g => String(g.id) === String(newGradeId));
    if (!grade) { setAddError("Pick a grade level."); return; }
    const ok = await saveNewSubject(grade);
    if (ok) {
      setNewOpen(false);
      setNewGradeId("");
    }
  }

  const facultyById = useMemo(() => Object.fromEntries((faculties || []).map(f => [f.id, f])), [faculties]);

  // Group subjects by gradeLevelId (null → "ungraded").
  const byGrade = useMemo(() => {
    const map = new Map();
    for (const sub of subjects) {
      const key = sub.gradeLevelId ?? "ungraded";
      if (!map.has(key)) map.set(key, []);
      map.get(key).push(sub);
    }
    return map;
  }, [subjects]);

  // Rows: every grade level (even empty ones) + an "Ungraded" row only if non-empty.
  const rows = useMemo(() => {
    const ordered = [...(gradeLevels || [])].sort((a, b) => {
      const oa = a.ordering ?? 999;
      const ob = b.ordering ?? 999;
      if (oa !== ob) return oa - ob;
      return (a.name || "").localeCompare(b.name || "");
    });
    const out = ordered.map(g => ({
      id: g.id,
      name: g.name,
      facultyId: g.departmentId ?? null,
      facultyName: facultyById[g.departmentId]?.name || null,
      subjects: (byGrade.get(g.id) || []).slice().sort((a, b) => (a.code || "").localeCompare(b.code || "")),
    }));
    const ungraded = byGrade.get("ungraded") || [];
    if (ungraded.length > 0) {
      out.push({
        id: "ungraded",
        name: "Ungraded",
        facultyId: null,
        facultyName: null,
        subjects: ungraded.slice().sort((a, b) => (a.code || "").localeCompare(b.code || "")),
      });
    }
    return out;
  }, [gradeLevels, byGrade, facultyById]);

  // Bucket rows under faculty headers so the "By Grade Level" view mirrors
  // the Sections panel: PRE-SCHOOL / GRADE SCHOOL / JUNIOR HIGH / SENIOR HIGH.
  // Faculty groups preserve the first-seen order in `rows` (already sorted
  // by grade ordering), which happens to give the natural school ladder.
  const rowsByFaculty = useMemo(() => {
    const map = new Map();
    for (const r of rows) {
      const key = r.id === "ungraded" ? "ungraded"
                : r.facultyId ?? "unassigned";
      if (!map.has(key)) map.set(key, []);
      map.get(key).push(r);
    }
    return Array.from(map.entries()).map(([facultyId, list]) => ({
      facultyId,
      facultyName:
        facultyId === "ungraded"   ? "Ungraded" :
        facultyId === "unassigned" ? "Unassigned department" :
        (facultyById[facultyId]?.name || "Unknown department"),
      rows: list,
    }));
  }, [rows, facultyById]);

  const openRow = openGradeId != null ? rows.find(r => r.id === openGradeId) : null;

  // Offering count per subject (used by the table view).
  const offeringsBySubject = useMemo(() => {
    const map = new Map();
    for (const o of offerings) {
      if (o.subjectId == null) continue;
      map.set(o.subjectId, (map.get(o.subjectId) || 0) + 1);
    }
    return map;
  }, [offerings]);

  const gradesById = useMemo(
    () => Object.fromEntries((gradeLevels || []).map(g => [g.id, g])),
    [gradeLevels]
  );

  const flatSubjects = useMemo(() => {
    const q = search.trim().toLowerCase();
    const enriched = (subjects || [])
      .map(sub => {
        const grade = sub.gradeLevelId != null ? gradesById[sub.gradeLevelId] : null;
        const facultyName = grade?.departmentId != null
          ? facultyById[grade.departmentId]?.name
          : (sub.departmentId != null ? facultyById[sub.departmentId]?.name : null);
        return {
          sub,
          gradeName:    grade?.name || "—",
          gradeOrder:   grade?.ordering ?? 9999,
          facultyName:  facultyName || "",
          offeringCount: offeringsBySubject.get(sub.id) || 0,
        };
      })
      .filter(row => {
        if (!q) return true;
        return (
          (row.sub.code  || "").toLowerCase().includes(q) ||
          (row.sub.title || "").toLowerCase().includes(q) ||
          row.gradeName.toLowerCase().includes(q) ||
          row.facultyName.toLowerCase().includes(q)
        );
      });
    // Selector + comparator for the active sort column. Numeric columns
    // compare numerically (with null pushed to the bottom); text columns
    // use a locale-aware string compare. The Grade column sorts by its
    // ordering field rather than alphabetically so "Grade 2" lands before
    // "Grade 10" naturally.
    const dir = sortDir === "asc" ? 1 : -1;
    const cmpStr = (a, b) => String(a || "").localeCompare(String(b || ""));
    const cmpNum = (a, b) => {
      const na = a == null ? Number.POSITIVE_INFINITY : Number(a);
      const nb = b == null ? Number.POSITIVE_INFINITY : Number(b);
      return na - nb;
    };
    const compareByKey = {
      code:      (a, b) => cmpStr(a.sub.code,  b.sub.code),
      title:     (a, b) => cmpStr(a.sub.title, b.sub.title),
      units:     (a, b) => cmpNum(a.sub.units, b.sub.units),
      grade:     (a, b) => a.gradeOrder - b.gradeOrder
                           || cmpStr(a.sub.code, b.sub.code),
      faculty:   (a, b) => cmpStr(a.facultyName, b.facultyName)
                           || a.gradeOrder - b.gradeOrder,
      offerings: (a, b) => cmpNum(a.offeringCount, b.offeringCount),
    };
    const cmp = compareByKey[sortKey] || compareByKey.grade;
    return enriched.slice().sort((a, b) => cmp(a, b) * dir);
  }, [subjects, search, gradesById, facultyById, offeringsBySubject, sortKey, sortDir]);

  // Header click handler — same column toggles direction, different
  // column resets direction to the column's natural default.
  function toggleSort(key, naturalDir = "asc") {
    setSortKey(prev => {
      if (prev === key) {
        setSortDir(d => (d === "asc" ? "desc" : "asc"));
        return prev;
      }
      setSortDir(naturalDir);
      return key;
    });
  }
  function sortArrow(key) {
    if (sortKey !== key) return "";
    return sortDir === "asc" ? " ▲" : " ▼";
  }

  return (
    <>
      {/* View toggle */}
      <div className={s.viewBar}>
        <div className={s.viewToggle}>
          <button
            type="button"
            className={`${s.toggleBtn} ${viewMode === "cards" ? s.toggleBtnActive : ""}`}
            onClick={() => setViewMode("cards")}>
            By Grade Level
          </button>
          <button
            type="button"
            className={`${s.toggleBtn} ${viewMode === "table" ? s.toggleBtnActive : ""}`}
            onClick={() => setViewMode("table")}>
            All Subjects
          </button>
        </div>
      </div>

      {viewMode === "table" && (
        <div className={s.tableView}>
          <div className={s.tableToolbar}>
            <input
              className={s.searchInput}
              type="text"
              value={search}
              onChange={e => setSearch(e.target.value)}
              placeholder="Search code, title, grade, or department…"
            />
            <button
              type="button"
              className={s.btnPrimary}
              onClick={() => {
                setAddError("");
                setAddCode(""); setAddTitle(""); setAddUnits("");
                setNewGradeId("");
                setNewOpen(true);
              }}>
              + New subject
            </button>
          </div>

          {flatSubjects.length === 0 ? (
            <div className={s.empty}>
              {search ? "No subjects match your search." : "No subjects in the catalog yet."}
            </div>
          ) : (
            <div className={s.tableWrap}>
              <table className={s.table}>
                <thead>
                  <tr>
                    <th className={s.sortable} onClick={() => toggleSort("code", "asc")}>
                      Code{sortArrow("code")}
                    </th>
                    <th className={s.sortable} onClick={() => toggleSort("title", "asc")}>
                      Title{sortArrow("title")}
                    </th>
                    <th className={`${s.numCol} ${s.sortable}`} onClick={() => toggleSort("units", "desc")}>
                      Units{sortArrow("units")}
                    </th>
                    <th className={s.sortable} onClick={() => toggleSort("grade", "asc")}>
                      Grade{sortArrow("grade")}
                    </th>
                    <th className={s.sortable} onClick={() => toggleSort("faculty", "asc")}>
                      Department{sortArrow("faculty")}
                    </th>
                    <th className={`${s.numCol} ${s.sortable}`} onClick={() => toggleSort("offerings", "desc")}>
                      Offerings{sortArrow("offerings")}
                    </th>
                    <th className={s.actionsCol}>Actions</th>
                  </tr>
                </thead>
                <tbody>
                  {flatSubjects.map(({ sub, gradeName, facultyName, offeringCount }) => (
                    <tr key={sub.id}>
                      <td><span className={s.codePill}>{sub.code || "?"}</span></td>
                      <td><b>{sub.title || "(Untitled)"}</b></td>
                      <td className={s.numCol}>{sub.units != null ? sub.units : "—"}</td>
                      <td>{gradeName}</td>
                      <td>{facultyName || "—"}</td>
                      <td className={`${s.numCol} ${offeringCount === 0 ? s.warnInline : ""}`}>{offeringCount}</td>
                      <td className={s.actionsCol}>
                        <button type="button" className={s.btnEdit} onClick={() => onEdit(sub)}>Edit</button>
                        <button type="button" className={s.btnDelete} onClick={() => onDelete(sub)}>Delete</button>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </div>
      )}

      {viewMode === "cards" && (
        rows.length === 0 ? (
          <div className={s.empty}>No grade levels yet. Create one under Grade Levels tab.</div>
        ) : rowsByFaculty.map(group => (
          <div key={group.facultyId} className={s.facultyGroup}>
            <div className={s.facultyHead}>{group.facultyName}</div>
            <div className={s.grid}>
              {group.rows.map(row => (
                <button
                  key={row.id}
                  type="button"
                  className={`${s.gradeCard} ${row.subjects.length === 0 ? s.gradeCardEmpty : ""}`}
                  onClick={() => setOpenGradeId(row.id)}>
                  <div className={s.gradeName}>{row.name}</div>
                  <div className={s.gradeCount}>
                    {row.subjects.length} subject{row.subjects.length === 1 ? "" : "s"}
                  </div>
                  {/* Faculty is already shown as the group header — drop it from
                      the card itself to avoid duplicate labels. */}
                  <div className={s.gradeHint}>View →</div>
                </button>
              ))}
            </div>
          </div>
        ))
      )}

      {openRow && createPortal(
        <div className={s.backdrop}>
          <div className={s.modal} onClick={e => e.stopPropagation()}>
            <div className={s.modalHead}>
              <div>
                <h3 className={s.modalTitle}>{openRow.name}</h3>
                <p className={s.modalSub}>
                  {openRow.subjects.length} subject{openRow.subjects.length === 1 ? "" : "s"}
                  {openRow.facultyName ? ` · ${openRow.facultyName}` : ""}
                </p>
              </div>
              <button type="button" className={s.closeBtn} onClick={() => setOpenGradeId(null)}>Close</button>
            </div>

            <div className={s.modalBody}>
              {addOpen && openRow.id !== "ungraded" ? (
                <form
                  className={s.addForm}
                  onSubmit={e => { e.preventDefault(); saveNewSubject(openRow); }}>
                  <div className={s.addContext}>
                    Adding to <b>{openRow.name}</b>
                    {openRow.facultyName ? <> · {openRow.facultyName}</> : null}
                  </div>
                  {addError && <div className={s.addError}>{addError}</div>}
                  <div className={s.addField}>
                    <label className={s.addLabel}>Copy from existing (optional)</label>
                    <select
                      className={s.addInput}
                      value=""
                      onChange={e => {
                        const id = Number(e.target.value);
                        if (!id) return;
                        const found = (subjects || []).find(x => x.id === id);
                        if (found) {
                          setAddCode(found.code || "");
                          setAddTitle(found.title || "");
                          if (found.units != null) setAddUnits(String(found.units));
                        }
                      }}>
                      <option value="">— type new below, or pick a template —</option>
                      {(subjects || []).slice().sort((a, b) => (a.code || "").localeCompare(b.code || "")).map(sub => (
                        <option key={sub.id} value={sub.id}>
                          {sub.code} — {sub.title}
                        </option>
                      ))}
                    </select>
                  </div>
                  <div className={s.addRow}>
                    <div className={s.addField}>
                      <label className={s.addLabel}>Code <span className={s.addReq}>*</span></label>
                      <input
                        className={s.addInput}
                        autoFocus
                        value={addCode}
                        onChange={e => setAddCode(e.target.value)}
                        placeholder="MATH5"
                        required
                      />
                    </div>
                    <div className={s.addField} style={{ maxWidth: 110 }}>
                      <label className={s.addLabel}>Units</label>
                      <input
                        className={s.addInput}
                        type="number"
                        step="0.5"
                        min="0"
                        value={addUnits}
                        onChange={e => setAddUnits(e.target.value)}
                        placeholder="1.0"
                      />
                    </div>
                  </div>
                  <div className={s.addField}>
                    <label className={s.addLabel}>Title <span className={s.addReq}>*</span></label>
                    <input
                      className={s.addInput}
                      value={addTitle}
                      onChange={e => setAddTitle(e.target.value)}
                      placeholder="Mathematics 5"
                      required
                    />
                  </div>
                </form>
              ) : openRow.subjects.length === 0 ? (
                <div className={s.empty}>No subjects for {openRow.name} yet.</div>
              ) : (
                <ul className={s.subjectList}>
                  {openRow.subjects.map(sub => (
                    <li key={sub.id} className={s.subjectRow}>
                      <div className={s.codePill}>{sub.code || "?"}</div>
                      <div className={s.subjectMain}>
                        <div className={s.subjectTitle}>{sub.title || "(Untitled)"}</div>
                        <div className={s.subjectMeta}>
                          {sub.units != null && <span>{sub.units} units</span>}
                        </div>
                      </div>
                      <div className={s.actions}>
                        <button
                          type="button"
                          className={s.btnEdit}
                          onClick={() => { setOpenGradeId(null); onEdit(sub); }}>
                          Edit
                        </button>
                        <button
                          type="button"
                          className={s.btnDelete}
                          onClick={() => onDelete(sub)}>
                          Delete
                        </button>
                      </div>
                    </li>
                  ))}
                </ul>
              )}
            </div>

            <div className={s.modalFoot}>
              {addOpen ? (
                <>
                  <button type="button" className={s.btnGhost} onClick={() => setAddOpen(false)} disabled={addBusy}>
                    Cancel
                  </button>
                  <button
                    type="button"
                    className={s.btnPrimary}
                    disabled={addBusy}
                    onClick={() => saveNewSubject(openRow)}>
                    {addBusy ? "Saving…" : "Create subject"}
                  </button>
                </>
              ) : openRow.id !== "ungraded" && (
                <button
                  type="button"
                  className={s.btnPrimary}
                  onClick={() => setAddOpen(true)}>
                  + Add subject to {openRow.name}
                </button>
              )}
            </div>
          </div>
        </div>,
        document.body
      )}

      {newOpen && createPortal(
        <div className={s.backdrop}>
          <div className={s.modal} onClick={e => e.stopPropagation()}>
            <div className={s.modalHead}>
              <div>
                <h3 className={s.modalTitle}>New subject</h3>
                <p className={s.modalSub}>Pick a grade level — the department is inherited from it.</p>
              </div>
              <button type="button" className={s.closeBtn} onClick={() => setNewOpen(false)}>Close</button>
            </div>

            <div className={s.modalBody}>
              <form className={s.addForm} onSubmit={e => { e.preventDefault(); saveNewFromTable(); }}>
                {addError && <div className={s.addError}>{addError}</div>}
                <div className={s.addField}>
                  <label className={s.addLabel}>Grade level <span className={s.addReq}>*</span></label>
                  <select
                    className={s.addInput}
                    value={newGradeId}
                    onChange={e => setNewGradeId(e.target.value)}
                    required>
                    <option value="">— select —</option>
                    {(gradeLevels || []).slice().sort((a, b) => (a.ordering ?? 0) - (b.ordering ?? 0)).map(g => (
                      <option key={g.id} value={g.id}>
                        {g.name}{facultyById[g.departmentId]?.name ? ` · ${facultyById[g.departmentId].name}` : ""}
                      </option>
                    ))}
                  </select>
                </div>
                <div className={s.addField}>
                  <label className={s.addLabel}>Copy from existing (optional)</label>
                  <select
                    className={s.addInput}
                    value=""
                    onChange={e => {
                      const id = Number(e.target.value);
                      if (!id) return;
                      const found = (subjects || []).find(x => x.id === id);
                      if (found) {
                        setAddCode(found.code || "");
                        setAddTitle(found.title || "");
                        if (found.units != null) setAddUnits(String(found.units));
                      }
                    }}>
                    <option value="">— type new below, or pick a template —</option>
                    {(subjects || []).slice().sort((a, b) => (a.code || "").localeCompare(b.code || "")).map(sub => (
                      <option key={sub.id} value={sub.id}>
                        {sub.code} — {sub.title}
                      </option>
                    ))}
                  </select>
                </div>
                <div className={s.addRow}>
                  <div className={s.addField}>
                    <label className={s.addLabel}>Code <span className={s.addReq}>*</span></label>
                    <input
                      className={s.addInput}
                      autoFocus
                      value={addCode}
                      onChange={e => setAddCode(e.target.value)}
                      placeholder="MATH5"
                      required
                    />
                  </div>
                  <div className={s.addField} style={{ maxWidth: 110 }}>
                    <label className={s.addLabel}>Units</label>
                    <input
                      className={s.addInput}
                      type="number"
                      step="0.5"
                      min="0"
                      value={addUnits}
                      onChange={e => setAddUnits(e.target.value)}
                      placeholder="1.0"
                    />
                  </div>
                </div>
                <div className={s.addField}>
                  <label className={s.addLabel}>Title <span className={s.addReq}>*</span></label>
                  <input
                    className={s.addInput}
                    value={addTitle}
                    onChange={e => setAddTitle(e.target.value)}
                    placeholder="Mathematics 5"
                    required
                  />
                </div>
              </form>
            </div>

            <div className={s.modalFoot}>
              <button type="button" className={s.btnGhost} onClick={() => setNewOpen(false)} disabled={addBusy}>
                Cancel
              </button>
              <button
                type="button"
                className={s.btnPrimary}
                disabled={addBusy}
                onClick={saveNewFromTable}>
                {addBusy ? "Saving…" : "Create subject"}
              </button>
            </div>
          </div>
        </div>,
        document.body
      )}
    </>
  );
}
